import { useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { type ColumnDef } from "@features/landing-page-analytics/types";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";

import { isColumnFilterable, isColumnSortable } from "../constants";

import { AboutPanelBenchmarks } from "./about-panel-benchmarks";
import { AboutPanelGeneric } from "./about-panel-generic";

interface Props {
  column: ColumnDef;
  onSelect(val: string): void;
}

export const ColumnMenu = ({ column, onSelect }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const [isAboutPanelVisible, setAboutPanelVisible] = useState(false);

  return (
    <Popover>
      {({ open }) => (
        <>
          <PopoverButton
            className={clsx(
              "flex justify-center items-center cursor-pointer",
              "rounded w-6 h-6 text-[0.65rem] ml-1",
              {
                "text-interstellar+2 hover:bg-cosmic-5": !open,
                "text-white bg-primary": open,
              },
            )}
          >
            <motion.i
              initial={false}
              animate={{ rotate: open ? 0 : 180 }}
              transition={{ duration: 0.2 }}
              className="fa-regular fa-sharp fa-chevron-up"
            />
          </PopoverButton>
          <PopoverPanel
            className={clsx(
              "absolute py-1 w-48 right-0 font-normal z-[1000]",
              "bg-white rounded-lg elevation-1 text-sm whitespace-normal",
            )}
          >
            {({ close }) => (
              <>
                {isAboutPanelVisible ? (
                  <>
                    {column.name === "cpm" ? (
                      <AboutPanelBenchmarks
                        onClose={() => {
                          prettyLogEventPage({
                            domain: "Landing Page Analytics",
                            action: "onClick",
                            additionalActionInfo: "Hide About This Metric Info",
                          });

                          setAboutPanelVisible(false);
                        }}
                      />
                    ) : (
                      <AboutPanelGeneric
                        metric={column.metric}
                        onClose={() => {
                          prettyLogEventPage({
                            domain: "Landing Page Analytics",
                            action: "onClick",
                            additionalActionInfo: "Hide About This Metric Info",
                          });

                          setAboutPanelVisible(false);
                        }}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {isColumnSortable(column.name) ? (
                      <>
                        <div
                          className={clsx(
                            "w-full text-left py-1.5 px-4 hover:bg-cosmic-4",
                            "truncate cursor-pointer",
                          )}
                          onClick={() => {
                            onSelect("desc");
                            close();
                          }}
                        >
                          <i
                            className={clsx(
                              "fa-regular fa-sharp fa-arrow-down text-[0.8rem]",
                              "text-primary mt-0.5 mr-1 w-4",
                            )}
                          />
                          Sort descending
                        </div>
                        <div
                          className={clsx(
                            "w-full text-left py-1.5 px-4 hover:bg-cosmic-4 truncate",
                            "cursor-pointer",
                          )}
                          onClick={() => {
                            onSelect("asc");
                            close();
                          }}
                        >
                          <i
                            className={clsx(
                              "fa-regular fa-sharp fa-arrow-up text-[0.8rem]",
                              "text-primary mt-0.5 mr-1 w-4",
                            )}
                          />
                          Sort ascending
                        </div>
                      </>
                    ) : null}
                    {isColumnFilterable(column.name) && (
                      <div
                        className={clsx(
                          "w-full text-left py-1.5 px-4 hover:bg-cosmic-4 truncate cursor-pointer",
                        )}
                        onClick={() => {
                          onSelect("filter");
                          close();
                        }}
                      >
                        <i
                          className={clsx(
                            "fa-regular fa-sharp fa-filter text-[0.8rem]",
                            "text-primary mt-0.5 mr-1 w-4",
                          )}
                        />
                        Filter
                      </div>
                    )}
                    <div
                      className={clsx(
                        "w-full text-left py-1.5 px-4 hover:bg-cosmic-4 truncate cursor-pointer",
                      )}
                      onClick={() => {
                        prettyLogEventPage({
                          domain: "Landing Page Analytics",
                          action: "onClick",
                          additionalActionInfo: "Show About This Metric Info",
                        });

                        setAboutPanelVisible(true);
                      }}
                    >
                      <i
                        className={clsx(
                          "fa-regular fa-sharp fa-circle-info text-[0.8rem]",
                          "text-primary mt-0.5 mr-1 w-4",
                        )}
                      />
                      About this metric
                    </div>
                  </>
                )}
              </>
            )}
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};
